/**
 * LanguageSwitcher — переключатель языка интерфейса (Фаза 4, итерация И6, §2.9).
 *
 * 0.14.2 (игротест, замечание 2): смена языка — **на лету**, без навигации на
 * поддомен и без перезагрузки. Прежний `window.location.assign(en.<apex>…)`
 * сохранял pathname, но полная перезагрузка на другой origin теряла состояние:
 * host-only cookie `kikayoo_session` (нет COOKIE_DOMAIN) → гейт `/me`/`/edit`
 * выкидывал на `/login`; sessionStorage host/player-флоу и localStorage — тоже
 * per-origin → пользователь оказывался «в начале, на главной». Теперь клик:
 * 1) `storeLocale(code)` — фиксация выбора в localStorage (переживает F5,
 *    приоритетнее поддомена при старте — см. [`resolveInitialLocale`](../i18n/locale.ts));
 * 2) `i18n.changeLanguage(code)` — мгновенный перевод UI; каталоги en/cn
 *    подгружаются лениво (resourcesToBackend в [`i18n/index.ts`](../i18n/index.ts)),
 *    Suspense уже стоит в main.tsx. Маршрут, query, скролл и состояние экрана
 *    не трогаются вовсе.
 * Поддомены `en.`/`cn.` остаются каналом дефолт-локали для новых посетителей
 * (без сохранённого выбора).
 *
 * Чистые хелперы локали — в [`i18n/locale.ts`](../i18n/locale.ts); опции и
 * apex-логика (для ThemeToggle) — в [`LanguageSwitcher.url.ts`](./LanguageSwitcher.url.ts).
 */
import { useTranslation } from 'react-i18next';
import { detectLocale, storeLocale, SUPPORTED_LOCALES, type Locale } from '../i18n/locale';
import { LANG_OPTIONS } from './LanguageSwitcher.url';

export interface LanguageSwitcherProps {
  /**
   * Текущая активная локаль для подсветки. По умолчанию берётся из
   * `i18n.resolvedLanguage` (реактивно — useTranslation перерендеривает после
   * `changeLanguage`), с фолбэком на поддомен через [`detectLocale`] (SSR/старт
   * до инициализации i18n). Проп опционален и нужен **только** для тестов
   * (проверить подсветку en/cn без смены языка); в проде всегда живое значение.
   */
  locale?: Locale;
}

/** Текущая локаль i18n, если она из поддерживаемых; иначе null. */
function resolvedI18nLocale(lng: string | undefined): Locale | null {
  return lng && (SUPPORTED_LOCALES as readonly string[]).includes(lng) ? (lng as Locale) : null;
}

/**
 * Компактная группа из трёх кнопок-переключателей языка. Ставится в футер
 * (см. [`App.tsx`](../App.tsx)). Активная опция помечена `aria-current="true"`
 * и классом `is-active`; клик по неактивной — мгновенная смена языка без
 * перезагрузки (пользователь остаётся на той же странице).
 */
export function LanguageSwitcher({ locale }: LanguageSwitcherProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const active: Locale =
    locale ??
    resolvedI18nLocale(i18n.resolvedLanguage) ??
    detectLocale(
      typeof window !== 'undefined' && typeof window.location !== 'undefined'
        ? window.location.hostname
        : '',
    );

  function select(code: Locale): void {
    // Уже на этой локали — смены не требуется.
    if (code === active) return;
    // 0.14.2: никаких window.location.assign — только на лету. Выбор фиксируем,
    // чтобы F5 не сбросил язык к поддоменному дефолту.
    storeLocale(code);
    void i18n.changeLanguage(code);
  }

  return (
    <span
      className="lang-switch"
      role="group"
      aria-label={t('lang.switch')}
      data-testid="lang-switch"
    >
      {LANG_OPTIONS.map((opt) => {
        const isActive = opt.code === active;
        return (
          <button
            key={opt.code}
            type="button"
            className={`lang-switch__btn${isActive ? ' is-active' : ''}`}
            onClick={() => select(opt.code)}
            // aria-current="true" на активной — скринридеры сообщат «текущий».
            aria-current={isActive ? 'true' : undefined}
            // Полная доступная метка: «Язык: Рус/EN/中文» (флаг — aria-hidden).
            aria-label={`${t('lang.switch')}: ${t(opt.labelKey)}`}
            data-testid={`lang-switch-${opt.code}`}
          >
            <span className="lang-switch__flag" aria-hidden="true">
              <img className="lang-switch__flag-img" src={opt.flag} alt="" width="20" height="15" />
            </span>
            <span className="lang-switch__label">{t(opt.labelKey)}</span>
          </button>
        );
      })}
    </span>
  );
}
